import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react'
import { Button, Stack, Table, TableBody, TableHead, TableRow, Typography, TableCell, TableContainer, Menu, MenuItem, Divider, ListItem, Box, CircularProgress } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { Security } from '../../proto/generated/security'
import websocketActions from '../../actions/websocket.actions'
import { Watchlist_Type, Watchlist_WatchlistItem_Type } from '../../proto/generated/watchlist'
import { EmptyState, EmptyStateAction, GhostRows } from '../common/EmptyState'
import { removewatchlistItem, setPreviewWatchlist as setPreviewWatchlistAction, setWatchlists, Watchlist, WatchlistItemData } from '../../reducer/watchList.reducer'
import { selectCommunityWatchlists, selectPreviewWatchlist, selectPreviewWatchlistId, selectWatchlists } from '../../selectors/watchList'
import WatchlistTableView, { WatchListDataForSort } from './WatchlistTableView'
import { makeStyles } from 'tss-react/mui'
import WatchListNormalView from './WatchListNormalView'
import { selectCustomizedColumns, selectNumberOfCustomizedWatchlistColumns, watchListView } from '../../selectors/user'
import { NonInteractiveTooltip } from '../custom'
import ThinArrowDownwardIcon from '../../assets/icons/ThinArrowDownwardIcon'
import ThinArrowUpwardIcon from '../../assets/icons/ThinArrowUpwardIcon'
import { calculateSnapshotDetails, getExchangeAndCodeFromKey, getSecurityAsync, getSecurityKey } from '../../util/security'
import { calculateSnapshotDetailsForExpression } from '../../util/expression'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { selectPrimarySecurity } from '../../selectors/home'
import useCheckAnyElementFocused from '../../hooks/useCheckAnyElementFocused'
import { selectCommonStore } from '../../selectors/common'
import { WidgetListItems } from '../../util/sidebar'
import WatchListGroupedPanel from './WatchListGroupedPanel'
import { deferAfterWatchlistHighlightPaint } from '../widgetDashboard/deferPaneSideEffects'
import { changeWatchlistSecurities, getSharedWatchlist } from '../../services/apis/watchlist'
import { isCommunityOrReferenceType, updateWatchlistInState } from './WatchUtils'
import { primary as primaryColor } from '../../assets/colors'
import { useShouldAllowAccess } from '../../hooks/useWatchlistAccess'
import { useAdminScreenerAccess } from '../../hooks/useAdminScreenerAccess'
import { fetchSecuritySnapshotsBulk } from '../../services/apis/candles.ts'
import store from '../../store'
import { EventTypes } from '../../assets/constants'
import eventBus from '../../events/eventBus'
import { SecurityClickEventData, SecurityClickSource } from '../../events/eventTypings'
import { matchPath } from 'react-router-dom'
import ROUTES from '../../assets/routes'
interface WatchListDataProps {
    addWatchListSection?: Function
    addOrRemoveWatchlistItem: Function
    setModalOpen: Function
    onDrop: Function
    editMode: boolean
}

const EMPTY_SNAPSHOTS: Record<string, never> = {}

const computeItemSortStats = (
    item: WatchlistItemData,
    snapshots: Record<string, unknown>,
    resolvedSortSecurities: Map<string, Security>
): WatchListDataForSort => {
    let stats: WatchListDataForSort = { ltp: 0, ltpChange: 0, changePercentage: 0, volume: 0 }
    if (item.item.type === Watchlist_WatchlistItem_Type.SECURITY && item.item.security) {
        const key = getSecurityKey(item.item.security.exchange, item.item.security.code)
        const security = resolvedSortSecurities.get(item.id) ?? item.item.security
        stats = calculateSnapshotDetails(security, snapshots[key] as any) || stats
    } else if (item.item.type === Watchlist_WatchlistItem_Type.EXPRESSION && item.item.securityExpression) {
        stats =
            calculateSnapshotDetailsForExpression(
                item.item.securityExpression.expression,
                item.item.securityExpression.securities,
                snapshots as any
            ) || stats
    }
    return stats
}
const useStyles = makeStyles()((theme) => ({
    stackRoot: {
        flex: 1,
        overflowY: 'auto',
        height: '100%'
    }
}))

export default memo(function WatchListData(props: WatchListDataProps) {
    const dispatch = useDispatch()
    const isAnyElementFocused = useCheckAnyElementFocused()
    const watchlists = useSelector(selectWatchlists)
    const [securitiesToSubscribe, setSecuritiesToSubscribe] = useState<Security[]>([])
    const [draggedOverItemIndex, setDraggedOverItemIndex] = useState<number | null>(null)
    // Refs keep drag-end handlers correct even when memoized rows skip re-renders.
    const draggedItemRef = useRef<WatchlistItemData | null>(null)
    const draggedOverItemIndexRef = useRef<number | null>(null)
    const itemsToDragRef = useRef<WatchlistItemData[]>([])
    const sortedWatchlistItemsRef = useRef<WatchlistItemData[]>([])
    const onDropRef = useRef(props.onDrop)
    const { classes } = useStyles()
    const [selectedItems, setSelectedItems] = useState<WatchlistItemData[]>([]);
    const tableView = useSelector(watchListView);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const snapshots = useSelector((state: RootState) =>
        sortColumn ? state.securityStore.snapshots : EMPTY_SNAPSHOTS
    )
    const customsizedColumnsTable = useSelector(selectCustomizedColumns)
    const { rightSideWidget } = useSelector(selectCommonStore)
    const isWatchlistOpen = rightSideWidget === WidgetListItems.WATCHLIST;
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const focusTriggeredByKeyboardRef = useRef(false);
    const keyboardFocusIndexRef = useRef<number | null>(null);
    const watchlistRootRef = useRef<HTMLDivElement>(null);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => { setAnchorEl(event.currentTarget); };
    const numberOfTableColumns = useSelector(selectNumberOfCustomizedWatchlistColumns)
    const shouldAllowAccess = useShouldAllowAccess();
    const shouldAllowAdminAccess = useAdminScreenerAccess();
    const communityWatchlists = useSelector(selectCommunityWatchlists);
    const previewId = useSelector(selectPreviewWatchlistId);
    const [previewWatchlist, setPreviewWatchlist] = useState<Watchlist | null>(null);
    const previousPreviewWatchlistIdRef = useRef<string | undefined>(undefined)
    const selectedWatchlistIdFromStore = useSelector(
        (state: RootState) => state.commonStore.watchlistId
    );
    const selectedWatchlistId = useMemo(() => {
        if (previewId.isActive && previewId.watchlistId) {
            return previewId.watchlistId;
        }
        return selectedWatchlistIdFromStore;
    }, [previewId, selectedWatchlistIdFromStore]);
    const selectedWatchlist = useMemo(() => {
        if (previewId.isActive && previewWatchlist) {
            return previewWatchlist
        }
        return watchlists[selectedWatchlistId]
    }, [previewId.isActive, previewWatchlist, watchlists, selectedWatchlistId])
    const watchlistRef = useRef<{initialLoaded: boolean;prevSrcWatchlistId?: string;}>({
        initialLoaded: false,
        prevSrcWatchlistId: undefined,
    });
    const SORT_OPTIONS = [
        { label: 'Change %', column: 'changePercentage', direction: 'asc' },
        { label: 'Change %', column: 'changePercentage', direction: 'desc' },
        { label: 'Change', column: 'change', direction: 'asc' },
        { label: 'Change', column: 'change', direction: 'desc' },
        { label: 'Last', column: 'last', direction: 'asc' },
        { label: 'Last', column: 'last', direction: 'desc' },
        { label: 'Symbol', column: 'symbol', direction: 'asc' },
        { label: 'Symbol', column: 'symbol', direction: 'desc' },
    ];

    const handleSort = (columnKey: string) => {
        if (sortColumn === columnKey) {
            setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortColumn(columnKey);
            setSortDirection('asc');
        }
    };

    const sortStatsRef = useRef<Map<string, WatchListDataForSort>>(new Map())
    const [sortStatsTick, setSortStatsTick] = useState(0)
    const [resolvedSortSecurities, setResolvedSortSecurities] = useState<Map<string, Security>>(new Map())

    const watchlistItemsKey = useMemo(
        () => selectedWatchlist?.watchlistItems?.map((item) => item.id).join(',') ?? '',
        [selectedWatchlist?.watchlistItems]
    )

    useEffect(() => {
        if (!sortColumn || !selectedWatchlist?.watchlistItems?.length) {
            setResolvedSortSecurities(new Map())
            return
        }
        let cancelled = false
        const items = selectedWatchlist.watchlistItems
        ;(async () => {
            const entries = await Promise.all(
                items.map(async (item) => {
                    if (item.item.type === Watchlist_WatchlistItem_Type.SECURITY && item.item.security) {
                        const key = getSecurityKey(item.item.security.exchange, item.item.security.code)
                        const security = (await getSecurityAsync(key)) ?? item.item.security
                        return [item.id, security] as const
                    }
                    return null
                })
            )
            if (cancelled) return
            setResolvedSortSecurities(new Map(entries.filter((entry): entry is readonly [string, Security] => entry !== null)))
        })()
        return () => {
            cancelled = true
        }
    }, [sortColumn, watchlistItemsKey, selectedWatchlist?.watchlistItems])

    useEffect(() => {
        if (!sortColumn || !selectedWatchlist?.watchlistItems?.length || focusTriggeredByKeyboardRef.current) {
            return
        }
        const nextStats = new Map<string, WatchListDataForSort>()
        for (const item of selectedWatchlist.watchlistItems) {
            nextStats.set(item.id, computeItemSortStats(item, snapshots, resolvedSortSecurities))
        }
        sortStatsRef.current = nextStats
        setSortStatsTick((tick) => tick + 1)
    }, [sortColumn, selectedWatchlist?.watchlistItems, snapshots, resolvedSortSecurities])

    const sortedWatchlistItems = useMemo(() => {
        const items = selectedWatchlist?.watchlistItems ?? []
        if (!sortColumn) return items

        const sortFn = (a: WatchlistItemData, b: WatchlistItemData) => {
            const aStats = sortStatsRef.current.get(a.id) ?? { ltp: 0, ltpChange: 0, changePercentage: 0, volume: 0 }
            const bStats = sortStatsRef.current.get(b.id) ?? { ltp: 0, ltpChange: 0, changePercentage: 0, volume: 0 }
            let aValue: number | string = 0
            let bValue: number | string = 0

            switch (sortColumn) {
                case 'symbol':
                    aValue = a.item.security?.symbol || ''
                    bValue = b.item.security?.symbol || ''
                    break
                case 'last':
                    aValue = aStats.ltp
                    bValue = bStats.ltp
                    break
                case 'change':
                    aValue = aStats.ltpChange
                    bValue = bStats.ltpChange
                    break
                case 'changePercentage':
                    aValue = aStats.changePercentage
                    bValue = bStats.changePercentage
                    break
                case 'volume':
                    aValue = aStats.volume
                    bValue = bStats.volume
                    break
            }

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue)
            }

            return sortDirection === 'asc'
                ? (aValue as number) - (bValue as number)
                : (bValue as number) - (aValue as number)
        }

        const result: WatchlistItemData[] = []
        const ungroupedItems: WatchlistItemData[] = []
        let currentGroup: WatchlistItemData[] = []

        const finalizeCurrentSectionGroup = () => {
            if (currentGroup.length === 0) return

            const [sectionItem, ...groupItems] = currentGroup
            const sectionName = sectionItem.item.sectionName?.toLowerCase() || ''
            const isDefaultSection = sectionName.includes('default')
            const validChildren = groupItems.filter((i) => i.item.type !== Watchlist_WatchlistItem_Type.SECTION)

            if (!isDefaultSection || validChildren.length > 0) {
                result.push(sectionItem)
                result.push(...validChildren.sort(sortFn))
            }

            currentGroup = []
        }

        for (const item of items) {
            if (item.item.type === Watchlist_WatchlistItem_Type.SECTION) {
                finalizeCurrentSectionGroup()
                currentGroup.push(item)
            } else if (currentGroup.length > 0) {
                currentGroup.push(item)
            } else {
                ungroupedItems.push(item)
            }
        }

        finalizeCurrentSectionGroup()
        return [...ungroupedItems.sort(sortFn), ...result]
    }, [selectedWatchlist?.watchlistItems, sortColumn, sortDirection, sortStatsTick])

    const flatFocusableItems = useMemo(() => {
        return sortedWatchlistItems.filter(
            (item) => item.item.type === Watchlist_WatchlistItem_Type.SECURITY || item.item.type === Watchlist_WatchlistItem_Type.EXPRESSION
        );
    }, [sortedWatchlistItems]);

    const flatFocusableItemsRef = useRef(flatFocusableItems)
    useEffect(() => {
        flatFocusableItemsRef.current = flatFocusableItems
    }, [flatFocusableItems])

    const selectedItemIds = useMemo(
        () => new Set(selectedItems.map((item) => item.id)),
        [selectedItems]
    )

    const securityPublishTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const pendingPublishIndexRef = useRef<number | null>(null)
    const keyboardHighlightRef = useRef<string | null>(null)

    const setKeyboardHighlight = useCallback((itemId: string | null, scrollIntoView = true) => {
        if (keyboardHighlightRef.current) {
            document
                .querySelector(`[data-watchlist-focus-id="${keyboardHighlightRef.current}"]`)
                ?.removeAttribute('data-keyboard-focused')
        }
        keyboardHighlightRef.current = itemId
        if (itemId) {
            watchlistRootRef.current?.setAttribute('data-watchlist-item-focused', 'true')
            const el = document.querySelector(`[data-watchlist-focus-id="${itemId}"]`)
            el?.setAttribute('data-keyboard-focused', 'true')
            if (scrollIntoView) {
                el?.scrollIntoView({ block: 'nearest' })
            }
        } else {
            watchlistRootRef.current?.removeAttribute('data-watchlist-item-focused')
        }
    }, [])

    const watchlistItemMatchesPrimaryKey = useCallback(
        (item: WatchlistItemData, primaryKey: string, expressionMode: boolean) => {
            if (item.item.type === Watchlist_WatchlistItem_Type.SECURITY && item.item.security) {
                return getSecurityKey(item.item.security.exchange, item.item.security.code) === primaryKey
            }
            if (item.item.type === Watchlist_WatchlistItem_Type.EXPRESSION && expressionMode && item.item.securityExpression) {
                return item.item.securityExpression.expression === primaryKey
            }
            return false
        },
        []
    )

    const clearDomHighlightWhenSynced = useCallback(
        (focusIndex: number) => {
            // During keyboard nav, isMatched borders are suppressed (data-keyboard-nav) — keep DOM highlight.
            if (focusTriggeredByKeyboardRef.current) return

            const item = flatFocusableItemsRef.current[focusIndex]
            if (!item || item.id !== keyboardHighlightRef.current) return

            const { key: primaryKey, expressionMode } = selectPrimarySecurity(store.getState())
            if (watchlistItemMatchesPrimaryKey(item, primaryKey, expressionMode)) {
                setKeyboardHighlight(null, false)
            }
        },
        [setKeyboardHighlight, watchlistItemMatchesPrimaryKey]
    )

    const publishFocusedItemSecurity = useCallback((focusIndex: number) => {
        if (!matchPath(ROUTES.chart, window.location.pathname)) return

        const item = flatFocusableItemsRef.current[focusIndex]
        if (!item) return

        const { key: primaryKey } = selectPrimarySecurity(store.getState())
        let clickEvent: SecurityClickEventData | null = null

        if (item.item.type === Watchlist_WatchlistItem_Type.SECURITY && item.item.security) {
            const security = item.item.security
            const key = getSecurityKey(security.exchange, security.code)
            if (key === primaryKey) {
                clearDomHighlightWhenSynced(focusIndex)
                return
            }
            window.securityManager.setSecurity(security)
            clickEvent = {
                key,
                securities: [security],
                expressionMode: false,
                source: SecurityClickSource.WATCHLIST
            }
        } else if (item.item.type === Watchlist_WatchlistItem_Type.EXPRESSION && item.item.securityExpression) {
            const { securityExpression } = item.item
            const key = securityExpression.expression
            if (key === primaryKey) {
                clearDomHighlightWhenSynced(focusIndex)
                return
            }
            securityExpression.securities.forEach((sec) => window.securityManager.setSecurity(sec))
            clickEvent = {
                key,
                securities: securityExpression.securities,
                expressionMode: true,
                source: SecurityClickSource.WATCHLIST
            }
        }

        if (clickEvent) {
            eventBus.publish(EventTypes.SECURITY_CLICKED, clickEvent)
            deferAfterWatchlistHighlightPaint(() => clearDomHighlightWhenSynced(focusIndex))
        } else {
            clearDomHighlightWhenSynced(focusIndex)
        }
    }, [clearDomHighlightWhenSynced])

    const scheduleSecurityPublish = useCallback(
        (focusIndex: number, debounceMs: number) => {
            pendingPublishIndexRef.current = focusIndex
            if (securityPublishTimerRef.current) {
                clearTimeout(securityPublishTimerRef.current)
                securityPublishTimerRef.current = null
            }

            const run = () => {
                if (pendingPublishIndexRef.current !== null) {
                    publishFocusedItemSecurity(pendingPublishIndexRef.current)
                }
            }

            if (debounceMs > 0) {
                securityPublishTimerRef.current = setTimeout(run, debounceMs)
            } else {
                deferAfterWatchlistHighlightPaint(run)
            }
        },
        [publishFocusedItemSecurity]
    )

    useEffect(() => {
        if (!isWatchlistOpen) return

        let prevPrimaryKey: string | undefined

        const syncFocusFromPrimary = () => {
            if (focusTriggeredByKeyboardRef.current) return

            const { primary, key: primaryKey, expressionMode } = selectPrimarySecurity(store.getState())
            if (primaryKey === prevPrimaryKey) return
            prevPrimaryKey = primaryKey

            const items = flatFocusableItemsRef.current
            if (!(primary || primaryKey) || !items.length) return

            const index = items.findIndex((item) => {
                if (item.item.type === Watchlist_WatchlistItem_Type.SECURITY && item.item.security) {
                    const sec = item.item.security
                    if (primary) {
                        return sec.exchange === primary.exchange && sec.code === primary.code
                    }
                    if (primaryKey) {
                        return getSecurityKey(sec.exchange, sec.code) === primaryKey
                    }
                    return false
                }
                if (item.item.type === Watchlist_WatchlistItem_Type.EXPRESSION && expressionMode && primaryKey) {
                    return item.item.securityExpression?.expression === primaryKey
                }
                return false
            })
            keyboardFocusIndexRef.current = index !== -1 ? index : null

            const highlightedId = keyboardHighlightRef.current
            if (highlightedId && primaryKey) {
                const highlightedItem = items.find((i) => i.id === highlightedId)
                if (highlightedItem && watchlistItemMatchesPrimaryKey(highlightedItem, primaryKey, expressionMode)) {
                    setKeyboardHighlight(null, false)
                }
            }
        }

        syncFocusFromPrimary()
        return store.subscribe(syncFocusFromPrimary)
    }, [isWatchlistOpen, setKeyboardHighlight, watchlistItemMatchesPrimaryKey])

    useEffect(() => {
        if (!selectedWatchlist?.shareId || communityWatchlists.length === 0) {
            return;
        }
        const srcWatchlist = communityWatchlists.find(
            (w) => w.shareId === selectedWatchlist.shareId
        );
        if (!srcWatchlist || !srcWatchlist.screenerId || srcWatchlist.screenerId.trim() === "") {
            return;
        }
        let interval: ReturnType<typeof setInterval> | undefined;
        const fetchFirstTime = async (isInitial = false) => {    
            if(!previewId.isActive){
                await updateWatchlistInState(selectedWatchlist.id , dispatch);
            }  
        };

        const shouldTriggerInitial =
            !watchlistRef.current.initialLoaded ||
            watchlistRef.current.prevSrcWatchlistId !== srcWatchlist.id;

        if (shouldTriggerInitial) {
            watchlistRef.current.initialLoaded = true;
            watchlistRef.current.prevSrcWatchlistId = srcWatchlist.id;
        }

        interval = setInterval(fetchFirstTime, 30000);
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [selectedWatchlist?.id, selectedWatchlist?.shareId, communityWatchlists]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isWatchlistOpen || isAnyElementFocused) return
            if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
            const items = flatFocusableItemsRef.current
            if (!items.length) return

            e.preventDefault()
            focusTriggeredByKeyboardRef.current = true
            watchlistRootRef.current?.setAttribute('data-keyboard-nav', 'true')

            const current = keyboardFocusIndexRef.current ?? 0
            const nextIndex =
                e.key === 'ArrowDown'
                    ? current < items.length - 1
                        ? current + 1
                        : 0
                    : current > 0
                      ? current - 1
                      : items.length - 1

            keyboardFocusIndexRef.current = nextIndex
            const nextItem = items[nextIndex]
            if (nextItem) {
                setKeyboardHighlight(nextItem.id)
            }

            pendingPublishIndexRef.current = nextIndex
            scheduleSecurityPublish(nextIndex, 250)
        };

        const handleMouseDown = (e: MouseEvent) => {
            if (watchlistRootRef.current?.contains(e.target as Node)) {
                return
            }
            focusTriggeredByKeyboardRef.current = false
            keyboardFocusIndexRef.current = null
            watchlistRootRef.current?.removeAttribute('data-keyboard-nav')
            setKeyboardHighlight(null)
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('mousedown', handleMouseDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('mousedown', handleMouseDown);
            if (securityPublishTimerRef.current) clearTimeout(securityPublishTimerRef.current)
        };
    }, [isWatchlistOpen, isAnyElementFocused, scheduleSecurityPublish, setKeyboardHighlight]);

    const lastFetchedPreviewIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (previewId.isActive && previewId.shareId) {
            if (lastFetchedPreviewIdRef.current === previewId.shareId) {
                // Already fetched this preview → skip
                return;
            }

            const fetchPreviewWatchlist = async () => {
                try {
                    const response = await getSharedWatchlist({ shareId: previewId.shareId! });

                    const processedItems = response.watchlistItems
                        .map((watchlistItem) => {
                            let itemId = '';
                            const { type, security, securityExpression, sectionName } = watchlistItem;

                            if (type === Watchlist_WatchlistItem_Type.SECURITY && security) {
                                itemId = getSecurityKey(security.exchange, security.code);
                            } else if (type === Watchlist_WatchlistItem_Type.EXPRESSION && securityExpression) {
                                itemId = securityExpression.expression;
                            } else if (type === Watchlist_WatchlistItem_Type.SECTION && sectionName !== undefined) {
                                itemId = sectionName;
                            }

                            return { id: itemId, item: watchlistItem };
                        })
                        .filter(({ id, item }) => {
                            const { type, security, securityExpression, sectionName } = item;
                            return (
                                id &&
                                ((type === Watchlist_WatchlistItem_Type.SECURITY && security) ||
                                    (type === Watchlist_WatchlistItem_Type.EXPRESSION &&
                                        securityExpression &&
                                        !securityExpression.expired &&
                                        securityExpression.securities.length > 0) ||
                                    (type === Watchlist_WatchlistItem_Type.SECTION && sectionName !== undefined))
                            );
                        });

                    const processedWatchlist: Watchlist = {
                        ...response,
                        watchlistItems: processedItems,
                        type: Array.isArray(response.type) ? response.type : [response.type],
                    };

                    setPreviewWatchlist(processedWatchlist);
                    dispatch(setPreviewWatchlistAction(processedWatchlist));

                    lastFetchedPreviewIdRef.current = previewId.shareId;
                } catch (error) {
                    console.error('Error fetching preview watchlist:', error);
                    setPreviewWatchlist(null);
                }
            };

            fetchPreviewWatchlist();
        } else {
            // Cleanup when preview closes
            setPreviewWatchlist(null);
            lastFetchedPreviewIdRef.current = null;
        }
    }, [previewId, dispatch]);

    const onClearKeyboardFocus = useCallback(() => {
        focusTriggeredByKeyboardRef.current = false
        keyboardFocusIndexRef.current = null
        setKeyboardHighlight(null)
        watchlistRootRef.current?.removeAttribute('data-keyboard-nav')
    }, [setKeyboardHighlight])

    const onItemActivate = useCallback(
        (item: WatchlistItemData) => {
            const index = flatFocusableItemsRef.current.findIndex((f) => f.id === item.id)
            if (index === -1) return

            keyboardFocusIndexRef.current = index
            focusTriggeredByKeyboardRef.current = false
            watchlistRootRef.current?.removeAttribute('data-keyboard-nav')
            setKeyboardHighlight(item.id, false)
            scheduleSecurityPublish(index, 0)
        },
        [setKeyboardHighlight, scheduleSecurityPublish]
    )

    const handleItemClick = useCallback(
        (e: React.MouseEvent, item: WatchlistItemData) => {
            setSelectedItems(prev => {
                const exists = prev.some(i => i.id === item.id);
                let newSelection: WatchlistItemData[];

                if (e.metaKey || e.ctrlKey) {

                    newSelection = exists
                        ? prev.filter(i => i.id !== item.id)
                        : [...prev, item];
                } else {
                    newSelection = [item];
                }

                return newSelection;

            });
            const index = flatFocusableItems.findIndex(f => f.id === item.id);
            if (index !== -1) {
                keyboardFocusIndexRef.current = index
                focusTriggeredByKeyboardRef.current = false
                watchlistRootRef.current?.removeAttribute('data-keyboard-nav')
                setKeyboardHighlight(null)
            }
        },
        [flatFocusableItems, setKeyboardHighlight]
    )
    useEffect(() => {
        if(previewId && previewId.isActive){
            const securityKeys: Set<string> = new Set()
            // Use selectedWatchlist which handles both preview and regular watchlists
            if (selectedWatchlist) {
                selectedWatchlist.watchlistItems.forEach((wlItem) => {
                    if (wlItem?.item?.type === Watchlist_WatchlistItem_Type.SECURITY && wlItem.item.security) {
                        securityKeys.add(getSecurityKey(wlItem.item.security.exchange, wlItem.item.security.code))
                    } else if (wlItem?.item?.type === Watchlist_WatchlistItem_Type.EXPRESSION && wlItem?.item?.securityExpression) {
                        wlItem.item.securityExpression!.securities.forEach((security: Security) => {
                            securityKeys.add(getSecurityKey(security.exchange, security.code))
                        })
                    }
                })
            }

            // Subscribe to security snapshots using the subscription tag
            if (securityKeys.size > 0) {
                websocketActions.setWebsocketSubscribeList(dispatch, {
                    securitySnapshotSubscriptions: [
                        {
                            tag: 'watchListSecuritySnapshots',
                            securities: Array.from(securityKeys).map((securityKey) => {
                                const [exc, code] = getExchangeAndCodeFromKey(securityKey)
                                return {
                                    exchange: exc,
                                    code: code
                                }
                            })
                        }
                    ]
                })

                // Fetch initial snapshots
                fetchSecuritySnapshotsBulk({
                    securities: Array.from(securityKeys)
                        .map((securityKey: string) => {
                            const [exc, code] = getExchangeAndCodeFromKey(securityKey)
                            return {
                                exchange: exc,
                                code: code
                            }
                        })
                        .filter((sec) => sec !== undefined),
                    dispatch: dispatch
                }).catch((error) => {
                    console.error('Error fetching security data', error)
                })
            } else {
                // Clear subscriptions when no securities
                websocketActions.setWebsocketSubscribeList(dispatch, {
                    securitySnapshotSubscriptions: []
                })
            }
        }
    }, [selectedWatchlist, dispatch]) 

    const subscribeOrderBook = useCallback((security: Security, subscribe: boolean) => {
        if (subscribe) {
            setSecuritiesToSubscribe([...securitiesToSubscribe, security])
        } else {
            const securityToRemove = securitiesToSubscribe.find(
                (listSecurity) => listSecurity.code === security.code
            )
            if (securityToRemove) {
                setSecuritiesToSubscribe(securitiesToSubscribe.filter(
                    (listSecurity) => listSecurity.code !== securityToRemove.code
                ))
            }
        }
    }, [securitiesToSubscribe])

    const handleSectionDelete = useCallback((
        watchlistItems: WatchlistItemData[],
        add: boolean,
        watchlistId: string
    ) => {
        if (!add && watchlistItems.length > 0) {
            const itemsToDelete = watchlistItems.map(w => w.item);
            changeWatchlistSecurities({
                watchlistItem: { watchlistItems: itemsToDelete },
                watchlistId,
                method: 'DELETE'
            }).then(() => {
                dispatch(removewatchlistItem({
                    itemId: itemsToDelete.map(i=>i.id),
                    watchlistId: watchlistId
                }))
            }).catch((err) => {
                console.error('Error deleting section:', err);
            });
        }
    }, [dispatch]);

    useEffect(() => {
        sortedWatchlistItemsRef.current = sortedWatchlistItems
    }, [sortedWatchlistItems])

    useEffect(() => {
        onDropRef.current = props.onDrop
    }, [props.onDrop])

    const onDragEnd = useCallback((dragImg: HTMLDivElement) => {
        return function () {
            if (document.body.contains(dragImg)) {
                document.body.removeChild(dragImg)
            }
            document.removeEventListener('dragend', onDragEnd(dragImg))
        }
    }, [])

    const handleDragStart = useCallback((e: React.DragEvent<HTMLLIElement>, watchlistItemData: WatchlistItemData) => {
        const isSelected = selectedItems.find(i => i.id === watchlistItemData.id);
        const itemsToDrag = isSelected ? selectedItems : [watchlistItemData];
        itemsToDragRef.current = itemsToDrag
        draggedItemRef.current = watchlistItemData
        const dragImg = document?.createElement('div')
        dragImg.style.padding = '0 15px'
        dragImg.style.width = '300px'
        dragImg.style.backgroundColor = '#1890ff22'
        dragImg.innerHTML = itemsToDrag
            .map(item => item.item.security?.symbol || item.item.sectionName || `ID: ${item.id}`)
            .join('<br/>');
        document?.body.appendChild(dragImg)
        e.dataTransfer.setDragImage(dragImg, -10, 0)
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('application/json', JSON.stringify(itemsToDrag));
        document.addEventListener('dragend', onDragEnd(dragImg))
    }, [onDragEnd, selectedItems])

    const handleDragOver = useCallback(
        (e: React.DragEvent<HTMLLIElement>, index: number | null) => {
            e.preventDefault()
            draggedOverItemIndexRef.current = index
            setDraggedOverItemIndex(index)
        },
        []
    )
    const handleDragEnd = useCallback(
        () => {
            const currentDraggedItem = draggedItemRef.current
            const currentDraggedOverIndex = draggedOverItemIndexRef.current
            const itemsToDrop = itemsToDragRef.current
            const items = sortedWatchlistItemsRef.current
            if (
                currentDraggedOverIndex !== null &&
                currentDraggedItem?.id !== items[currentDraggedOverIndex]?.id &&
                itemsToDrop.length > 0
            ) {
                onDropRef.current(itemsToDrop, items[currentDraggedOverIndex])
            }
            setSelectedItems([])
            draggedItemRef.current = null
            itemsToDragRef.current = []
            draggedOverItemIndexRef.current = null
            setDraggedOverItemIndex(null)
        },
        []
    )
    const onAddSymbolButtonClick = useCallback(() => {
        props.setModalOpen(true)
    }, [props])

    const canAddSymbols = useMemo(
        () =>
            !!(
                selectedWatchlist &&
                shouldAllowAccess(selectedWatchlist, props.editMode) &&
                shouldAllowAdminAccess(selectedWatchlist?.shareId)
            ),
        [selectedWatchlist, props.editMode, shouldAllowAccess, shouldAllowAdminAccess]
    )

    const isCommunityEmptyWatchlist = useMemo(() => {
        if (!selectedWatchlist) return false
        if (isCommunityOrReferenceType(selectedWatchlist)) return true
        return !!(selectedWatchlist.type?.includes(Watchlist_Type.REFERENCE) && selectedWatchlist.shareId)
    }, [selectedWatchlist])

    const renderAddSymbolFooter = () => {
        if (!canAddSymbols) return null
        return (
            <Box sx={{ width: '100%', flexShrink: 0 }}>
                <Button
                    fullWidth
                    variant="text"
                    color="inherit"
                    onClick={onAddSymbolButtonClick}
                    sx={{ textTransform: 'none', fontSize: '14px', py: 0, minHeight: 'unset', justifyContent: 'center' }}
                >
                    + Add symbol
                </Button>
            </Box>
        )
    }

    const renderEmptyWatchlist = (requireWatchlistsLoaded?: boolean) => {
        const readOnlyCommunityList = isCommunityEmptyWatchlist && !canAddSymbols
        const showAddSymbol =
            !readOnlyCommunityList && canAddSymbols && (!requireWatchlistsLoaded || Object.keys(watchlists).length > 0)
        return (
            <EmptyState
                preview={<GhostRows variant="watchlist" />}
                action={showAddSymbol ? <EmptyStateAction label="Add symbol" onClick={onAddSymbolButtonClick} /> : undefined}
                text={
                    readOnlyCommunityList
                        ? 'There are no symbols in this community list.'
                        : 'Add the symbols you follow to track prices, alerts, and news in one place.'
                }
            />
        )
    }

    return (
        <Stack ref={watchlistRootRef} className={`${classes.stackRoot} watchlist-data-root`}>
            {tableView && selectedWatchlist?.watchlistItems?.length > 0 ? (
                <TableContainer style={{ overflowX: 'hidden', width: '100%', height: '100%' }}>
                    <Table stickyHeader style={{ borderCollapse: 'separate', fontSize: 13, height: 'auto' }}>
                        <TableHead>
                            <TableRow
                                sx={{
                                    height: 32,
                                    '& th': {
                                        paddingY: '4px',
                                        paddingX: '8px',
                                        fontSize: 12,
                                        lineHeight: 1.2
                                    }
                                }}
                            >
                                <TableCell
                                    component="th"
                                    sx={{ borderBottomColor: (theme) => theme.palette.divider }}
                                    style={{ padding: '8px', paddingLeft: '15px', width: numberOfTableColumns === 1 ? '100%' : '120px' }}
                                >
                                    <NonInteractiveTooltip
                                        title={
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                Click to sort by symbol
                                                {sortColumn === 'symbol' ? (
                                                    sortDirection === 'asc' ? (
                                                        <ThinArrowUpwardIcon style={{ fontSize: '14px' }} />
                                                    ) : (
                                                        <ThinArrowDownwardIcon style={{ fontSize: '14px' }} />
                                                    )
                                                ) : (
                                                    <ThinArrowDownwardIcon style={{ fontSize: '14px' }} />
                                                )}
                                            </span>
                                        }
                                        arrow
                                        placement="top"
                                    >
                                        <div
                                            style={{
                                                fontSize: '13px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                                fontWeight: sortColumn === 'symbol' ? 'bold' : 'normal',
                                                width: '100%'
                                            }}
                                            onClick={() => handleSort('symbol')}
                                        >
                                            Symbol
                                            {sortColumn === 'symbol' &&
                                                (sortDirection === 'asc' ? <ThinArrowDownwardIcon style={{ fontSize: '14px' }} /> : <ThinArrowUpwardIcon style={{ fontSize: '14px' }} />)}
                                        </div>
                                    </NonInteractiveTooltip>
                                </TableCell>
                                {customsizedColumnsTable.last ? (
                                    <TableCell
                                        sx={{ borderBottomColor: (theme) => theme.palette.divider }}
                                        style={{
                                            textAlign: 'right'
                                        }}
                                    >
                                        <NonInteractiveTooltip
                                            title={
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    Click to sort by last
                                                    {sortColumn === 'last' ? (
                                                        sortDirection === 'asc' ? (
                                                            <ThinArrowUpwardIcon style={{ fontSize: '14px' }} />
                                                        ) : (
                                                            <ThinArrowDownwardIcon style={{ fontSize: '14px' }} />
                                                        )
                                                    ) : (
                                                        <ThinArrowDownwardIcon style={{ fontSize: '14px' }} />
                                                    )}
                                                </span>
                                            }
                                            arrow
                                            placement="top"
                                        >
                                            <div
                                                style={{
                                                    fontSize: '13px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                    fontWeight: sortColumn === 'last' ? 'bold' : 'normal',
                                                    width: '100%',
                                                    justifyContent: 'flex-end'
                                                }}
                                                onClick={() => handleSort('last')}
                                            >
                                                Last
                                                {sortColumn === 'last' &&
                                                    (sortDirection === 'asc' ? <ThinArrowDownwardIcon style={{ fontSize: '14px' }} /> : <ThinArrowUpwardIcon style={{ fontSize: '14px' }} />)}
                                            </div>
                                        </NonInteractiveTooltip>
                                    </TableCell>
                                ) : null}

                                {customsizedColumnsTable.change && (
                                    <TableCell
                                        component="th"
                                        style={{
                                            textAlign: 'right',
                                            borderBottom: 'none'
                                        }}
                                    >
                                        <NonInteractiveTooltip
                                            title={
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    Click to sort by change
                                                    {sortColumn === 'change' ? (
                                                        sortDirection === 'asc' ? (
                                                            <ThinArrowUpwardIcon style={{ fontSize: '14px' }} />
                                                        ) : (
                                                            <ThinArrowDownwardIcon style={{ fontSize: '14px' }} />
                                                        )
                                                    ) : (
                                                        <ThinArrowDownwardIcon style={{ fontSize: '14px' }} />
                                                    )}
                                                </span>
                                            }
                                            arrow
                                            placement="top"
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                    fontWeight: sortColumn === 'change' ? 'bold' : 'normal',
                                                    width: '100%',
                                                    justifyContent: 'flex-end',
                                                    fontSize: '13px'
                                                }}
                                                onClick={() => handleSort('change')}
                                            >
                                                Chg
                                                {sortColumn === 'change' &&
                                                    (sortDirection === 'asc' ? <ThinArrowDownwardIcon style={{ fontSize: '14px' }} /> : <ThinArrowUpwardIcon style={{ fontSize: '14px' }} />)}
                                            </div>
                                        </NonInteractiveTooltip>
                                    </TableCell>
                                )}
                                {customsizedColumnsTable.changePercentage && (
                                    <TableCell
                                        component="th"
                                        style={{
                                            textAlign: 'right',
                                            borderBottom: 'none'
                                        }}
                                    >
                                        <NonInteractiveTooltip
                                            title={
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    Click to sort by Chg%
                                                    {sortColumn === 'changePercentage' ? (
                                                        sortDirection === 'asc' ? (
                                                            <ThinArrowUpwardIcon style={{ fontSize: '14px' }} />
                                                        ) : (
                                                            <ThinArrowDownwardIcon style={{ fontSize: '14px' }} />
                                                        )
                                                    ) : (
                                                        <ThinArrowDownwardIcon style={{ fontSize: '14px' }} />
                                                    )}
                                                </span>
                                            }
                                            arrow
                                            placement="top"
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                    fontWeight: sortColumn === 'changePercentage' ? 'bold' : 'normal',
                                                    width: '100%',
                                                    justifyContent: 'flex-end',
                                                    fontSize: '13px'
                                                }}
                                                onClick={() => handleSort('changePercentage')}
                                            >
                                                Chg%
                                                {sortColumn === 'changePercentage' &&
                                                    (sortDirection === 'asc' ? <ThinArrowDownwardIcon style={{ fontSize: '14px' }} /> : <ThinArrowUpwardIcon style={{ fontSize: '14px' }} />)}
                                            </div>
                                        </NonInteractiveTooltip>
                                    </TableCell>
                                )}
                                {customsizedColumnsTable.volume && (
                                    <TableCell
                                        component="th"
                                        style={{
                                            textAlign: 'right',
                                            borderBottom: 'none',
                                            paddingRight: '20px'
                                        }}
                                    >
                                        <NonInteractiveTooltip
                                            title={
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    Click to sort by volume
                                                    {sortColumn === 'volume' ? (
                                                        sortDirection === 'asc' ? (
                                                            <ThinArrowUpwardIcon style={{ fontSize: '14px' }} />
                                                        ) : (
                                                            <ThinArrowDownwardIcon style={{ fontSize: '14px' }} />
                                                        )
                                                    ) : (
                                                        <ThinArrowDownwardIcon style={{ fontSize: '14px' }} />
                                                    )}
                                                </span>
                                            }
                                            arrow
                                            placement="top"
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                    fontWeight: sortColumn === 'volume' ? 'bold' : 'normal',
                                                    width: '100%',
                                                    justifyContent: 'flex-end',
                                                    fontSize: '13px'
                                                }}
                                                onClick={() => handleSort('volume')}
                                            >
                                                Vol
                                                {sortColumn === 'volume' &&
                                                    (sortDirection === 'asc' ? <ThinArrowDownwardIcon style={{ fontSize: '14px' }} /> : <ThinArrowUpwardIcon style={{ fontSize: '14px' }} />)}
                                            </div>
                                        </NonInteractiveTooltip>
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody
                            sx={{
                                height: '100%'
                            }}
                        >
                            {selectedWatchlist?.watchlistItems?.length > 0 ? (
                                sortedWatchlistItems.some((item) => item.item.type === Watchlist_WatchlistItem_Type.SECTION) ? (
                                    <WatchListGroupedPanel
                                        data={sortedWatchlistItems}
                                        draggedOverItem={draggedOverItemIndex !== null ? sortedWatchlistItems[draggedOverItemIndex] : null}
                                        selectedWatchlistId={selectedWatchlistId}
                                        handleWatchlistItemAddOrDelete={handleSectionDelete}
                                        subscribeOrderBook={subscribeOrderBook}
                                        handleDragOver={handleDragOver}
                                        handleDragEnd={handleDragEnd}
                                        handleDragStart={handleDragStart}
                                        onItemClick={handleItemClick as any}
                                        onItemActivate={onItemActivate}
                                        selectedItemIds={selectedItemIds}
                                        totalItems={flatFocusableItems.length}
                                        editMode={props.editMode}
                                        selectedWatchlist={selectedWatchlist}
                                        onClearFocus={onClearKeyboardFocus}
                                    />
                                ) : (
                                    sortedWatchlistItems.map((data, index) => {
                                        const isDraggedOver = draggedOverItemIndex === index
                                        return (
                                            <WatchlistTableView
                                                key={data.id}
                                                isDraggedOver={draggedOverItemIndex !== null && sortedWatchlistItems[draggedOverItemIndex]?.id === data.id}
                                                data={data}
                                                watchlistId={selectedWatchlistId}
                                                addOrRemoveWatchlistDataItem={handleSectionDelete}
                                                subscribeSecurity={subscribeOrderBook}
                                                handleDragOver={handleDragOver}
                                                handleDragEnd={handleDragEnd}
                                                handleDragStart={handleDragStart}
                                                index={index}
                                                onItemClick={handleItemClick as any}
                                                onItemActivate={onItemActivate}
                                                selectedItemIds={selectedItemIds}
                                                showBottomDragLine={isDraggedOver}
                                                editMode={props.editMode}
                                                onClearFocus={onClearKeyboardFocus}
                                            />
                                        )
                                    })
                                )
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5}>{renderEmptyWatchlist()}</TableCell>
                                </TableRow>
                            )}
                            {
                                <TableRow sx={{ height: '1px', boxSizing: 'border-box' }}>
                                    {draggedOverItemIndex === sortedWatchlistItems.length ? (
                                        <TableCell colSpan={numberOfTableColumns} padding={'none'} sx={{ maxHeight: '1px', borderBottom: 'none' }}>
                                            <div style={{ background: primaryColor, height: '1px' }}></div>
                                        </TableCell>
                                    ) : null}
                                </TableRow>
                            }
                            {canAddSymbols && (
                                <TableRow>
                                    <TableCell colSpan={numberOfTableColumns} sx={{ borderBottom: 'none', py: 0, pt: 0, px: 0 }}>
                                        {renderAddSymbolFooter()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : selectedWatchlist?.watchlistItems?.length > 0 ? (
                <>
                    <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        justifyContent="space-between"
                        height={'32px'}
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                            background: (theme) => theme.palette.background.default,
                            padding: '8px 8px 8px 20px',
                            borderBottom: '1px solid',
                            borderColor: (theme) => theme.palette.divider
                        }}
                    >
                        <Typography fontSize={'14px'}>Symbol</Typography>
                        {shouldAllowAccess(selectedWatchlist, props.editMode) && shouldAllowAdminAccess(selectedWatchlist?.shareId) && (
                            <Stack direction="row" alignItems="center" onClick={handleClick} sx={{ cursor: 'pointer', paddingRight: '5px' }}>
                                <Typography fontSize={'14px'}>Sort by</Typography>
                                {anchorEl ? <ExpandLess /> : <ExpandMore />}
                            </Stack>
                        )}
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={() => setAnchorEl(null)}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right'
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right'
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ px: 1, py: 1, color: 'text.secondary' }}>
                                Customized order
                            </Typography>
                            <Divider />

                            {SORT_OPTIONS.map((option, index) => {
                                const isActive = sortColumn === option.column && sortDirection === option.direction
                                const isAsc = sortDirection === 'asc'

                                return (
                                    <React.Fragment key={`${option.column}-${option.direction}`}>
                                        <MenuItem
                                            onClick={() => {
                                                if (isActive) {
                                                    setSortDirection(isAsc ? 'desc' : 'asc')
                                                } else {
                                                    setSortColumn(option.column)
                                                    setSortDirection(option.direction as any)
                                                }
                                                setAnchorEl(null)
                                            }}
                                            sx={{ display: 'flex', justifyContent: 'space-between' }}
                                        >
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                {option.direction === 'asc' ? <ThinArrowUpwardIcon style={{ fontSize: '14px' }} /> : <ThinArrowDownwardIcon style={{ fontSize: '14px' }} />}
                                                <Typography fontSize="14px" fontWeight={500}>
                                                    {option.label}
                                                </Typography>
                                            </Stack>
                                        </MenuItem>

                                        {[1, 3, 5].includes(index) && <Divider sx={{ marginTop: '0px ! important', marginBottom: '0px !important' }} />}
                                    </React.Fragment>
                                )
                            })}
                        </Menu>
                    </Stack>

                    {
                        <>
                            {sortedWatchlistItems.some((item) => item.item.type === Watchlist_WatchlistItem_Type.SECTION) ? (
                                <WatchListGroupedPanel
                                    data={sortedWatchlistItems}
                                    draggedOverItem={draggedOverItemIndex !== null ? sortedWatchlistItems[draggedOverItemIndex] : null}
                                    selectedWatchlistId={selectedWatchlistId}
                                    handleWatchlistItemAddOrDelete={handleSectionDelete}
                                    subscribeOrderBook={subscribeOrderBook}
                                    handleDragOver={handleDragOver}
                                    handleDragEnd={handleDragEnd}
                                    handleDragStart={handleDragStart}
                                    onItemClick={handleItemClick as any}
                                    onItemActivate={onItemActivate}
                                    selectedItemIds={selectedItemIds}
                                    totalItems={flatFocusableItems.length}
                                    editMode={props.editMode}
                                    selectedWatchlist={selectedWatchlist}
                                    onClearFocus={onClearKeyboardFocus}
                                />
                            ) : (
                                sortedWatchlistItems.map((data, index) => (
                                    <WatchListNormalView
                                        key={data.id}
                                        isDraggedOver={draggedOverItemIndex !== null && sortedWatchlistItems[draggedOverItemIndex]?.id === data.id}
                                        data={data}
                                        watchlistId={selectedWatchlistId}
                                        addOrRemoveWatchlistDataItem={handleSectionDelete}
                                        subscribeSecurity={subscribeOrderBook}
                                        handleDragOver={handleDragOver}
                                        handleDragEnd={handleDragEnd}
                                        handleDragStart={handleDragStart}
                                        index={index}
                                        onItemClick={handleItemClick as any}
                                        onItemActivate={onItemActivate}
                                        selectedItemIds={selectedItemIds}
                                        editMode={props.editMode}
                                        onClearFocus={onClearKeyboardFocus}
                                    />
                                ))
                            )}
                            {draggedOverItemIndex === sortedWatchlistItems.length ? <ListItem sx={{ height: '1px', background: primaryColor, p: 0, boxSizing: 'border-box' }}></ListItem> : null}
                            {renderAddSymbolFooter()}
                        </>
                    }
                </>
            ) : (
                renderEmptyWatchlist(true)
            )}
        </Stack>
    )
})
